import api from '../http.service';
import { API_ENDPOINTS } from '@/config/api.config';

export interface User {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  /** Agent role: assigned bot UUIDs; empty/unset = all bots in tenant */
  agent_bot_ids?: string[];
  email: string;
  full_name: string;
  role: string;
  status: string;
  avatar_url?: string;
  last_login?: string;
  must_change_password?: boolean;
  menu_access?: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface UserCredentials {
  email: string;
  tempPassword: string;
  platformUrl: string;
  expiresIn: string;
}

export interface CreateUserData {
  email: string;
  fullName: string;
  role?: string;
  tenantId?: string;
  agentBotIds?: string[];
  menuAccess?: string[] | null;
}

export interface UpdateUserData {
  email?: string;
  fullName?: string;
  role?: string;
  status?: string;
  tenantId?: string;
  agentBotIds?: string[];
  menuAccess?: string[] | null;
}

export interface UserListResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CreateUserResponse {
  user: User;
  credentials: UserCredentials;
}

// List users
export const listUsers = async (params?: {
  tenantId?: string;
  role?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
  /** When true, only agents available for workspace (excludes agentWorkspaceEnabled=false in preferences). */
  forAgentWorkspace?: boolean;
}): Promise<UserListResponse> => {
  const response = await api.get(API_ENDPOINTS.STUDIO.USERS, { params });
  return response.data.data;
};

// Get user by ID
export const getUser = async (id: string): Promise<User> => {
  const response = await api.get(`${API_ENDPOINTS.STUDIO.USERS}/${id}`);
  return response.data.data;
};

// Create user
export const createUser = async (data: CreateUserData): Promise<CreateUserResponse> => {
  const response = await api.post(API_ENDPOINTS.STUDIO.USERS, data);
  return response.data.data;
};

// Update user
export const updateUser = async (id: string, data: UpdateUserData): Promise<User> => {
  const response = await api.put(`${API_ENDPOINTS.STUDIO.USERS}/${id}`, data);
  return response.data.data;
};

// Delete user
export const deleteUser = async (id: string): Promise<void> => {
  await api.delete(`${API_ENDPOINTS.STUDIO.USERS}/${id}`);
};

// Reset user password
export const resetUserPassword = async (id: string): Promise<{ credentials: UserCredentials }> => {
  const response = await api.post(`${API_ENDPOINTS.STUDIO.USERS}/${id}/reset-password`);
  return response.data.data;
};

// Change own password
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await api.post(`${API_ENDPOINTS.STUDIO.USERS}/change-password`, {
    currentPassword,
    newPassword,
  });
};

export default {
  listUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  changePassword,
};

